import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { AuditionPhaseModal } from "./AuditionPhaseModal";
import { OpportunityModalHeader } from "./opportunity-details/OpportunityModalHeader";
import { OpportunityModalContent } from "./opportunity-details/OpportunityModalContent";
import { OpportunityModalActions } from "./opportunity-details/OpportunityModalActions";

interface OpportunityDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  invitationId: string;
}

export const OpportunityDetailsModal = ({ 
  isOpen, 
  onClose, 
  opportunityId,
  invitationId 
}: OpportunityDetailsModalProps) => {
  const { toast } = useToast();
  const [showAuditionPhase, setShowAuditionPhase] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data: opportunityDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['opportunity-details', opportunityId],
    queryFn: async () => {
      console.log('Fetching opportunity details for ID:', opportunityId);
      const { data, error } = await supabase
        .from('opportunities')
        .select(`
          *,
          profiles:created_by (
            full_name,
            avatar_url,
            bio,
            company,
            job_title,
            location
          )
        `)
        .eq('id', opportunityId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching opportunity:', error);
        throw error;
      }

      console.log('Fetched opportunity details:', data);
      return data;
    },
    enabled: !!opportunityId,
  });

  const { data: scriptData } = useQuery({
    queryKey: ['opportunity-script', opportunityId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('opportunity_scripts')
        .select('script_url')
        .eq('opportunity_id', opportunityId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!opportunityId,
  });

  const handleAcceptInvitation = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('opportunity_invitations')
        .update({
          status: 'accepted'
        })
        .eq('id', invitationId);

      if (error) throw error;
      setShowAuditionPhase(true);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: "Error",
        description: "Failed to accept invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeclineInvitation = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('opportunity_invitations')
        .update({
          status: 'declined'
        })
        .eq('id', invitationId);

      if (error) throw error;

      toast({
        title: "Invitation declined",
        description: "You've declined the invitation.",
      });

      onClose();
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast({
        title: "Error",
        description: "Failed to decline invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          {isLoadingDetails ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : opportunityDetails ? (
            <div className="space-y-6">
              <OpportunityModalHeader profileData={opportunityDetails.profiles} />
              <OpportunityModalContent opportunity={opportunityDetails} />
              <OpportunityModalActions
                onAccept={handleAcceptInvitation}
                onDecline={handleDeclineInvitation}
                isLoading={isLoading}
              />
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Opportunity not found
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AuditionPhaseModal
        isOpen={showAuditionPhase}
        onClose={() => {
          setShowAuditionPhase(false);
          onClose();
        }}
        opportunityId={opportunityId}
        invitationId={invitationId}
        scriptUrl={scriptData?.script_url}
      />
    </>
  );
};